import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import { ButtonOBSOLETED, Notification, Message } from '../../web.common';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';

const STATUS_OPTIONS = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Declined', label: 'Declined' },
];

// Aligns with Lib.Common Category enum bit values
const SPORT_NAMES = {
  1: 'Football',
  2: 'Badminton',
  4: 'Tennis',
  8: 'Squash',
  16: 'Volleyball',
  32: 'Basketball',
  4096: 'Table Tennis',
  8192: 'Padel',
};

const KIND_NAMES = {
  1: 'Education',
  2: 'Workplace',
  3: 'Club',
  4: 'Neighborhood',
};

const STATUS_LABELS = {
  0: 'Pending',
  1: 'Approved',
  2: 'Declined',
};

const AmbassadorApplicationsInternal = (props) => {
  const [status, setStatus] = useState('Pending');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await props.api.ambassadorApplications.list({ status, page: 1, count: 100 });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setNotification(new Message('error', 'Unable to load applications'));
    } finally {
      setIsLoading(false);
    }
  }, [props.api, status]);

  useEffect(() => {
    load();
  }, [load]);

  // 409 { reason: "not_pending", status } when someone already decided it.
  const notPending = (e) => (e?.payload?.responseData?.reason === 'not_pending'
    ? `: the application is no longer pending (${e.payload.responseData.status})`
    : (e?.payload?.translatedMessage ? `: ${e.payload.translatedMessage}` : ''));

  const handleApprove = async (id) => {
    setActingId(id);
    try {
      await props.api.ambassadorApplications.approve(id);
      setNotification(new Message('success', 'Approved'));
      await load();
    } catch (e) {
      setNotification(new Message('error', `Approve failed${notPending(e)}`));
    } finally {
      setActingId(null);
    }
  };

  const handleDecline = async (id) => {
    if (!window.confirm('Decline this application?')) return;
    setActingId(id);
    try {
      await props.api.ambassadorApplications.decline(id);
      setNotification(new Message('warning', 'Declined'));
      await load();
    } catch (e) {
      setNotification(new Message('error', `Decline failed${notPending(e)}`));
    } finally {
      setActingId(null);
    }
  };

  const formatSport = (cat) => SPORT_NAMES[cat] || `Category ${cat}`;
  const formatKind = (kind) => KIND_NAMES[kind] || `Kind ${kind}`;
  const formatStatus = (statusInt) => STATUS_LABELS[statusInt] || `Status ${statusInt}`;
  const formatDate = (iso) => iso ? new Date(iso).toLocaleString() : '';

  return (
    <>
      <Notification newMessage={notification} />
      <PageTop
        documentTitle='Ambassador applications'
        documentDescription='Review ambassador applications'
        mainHeadingContent='Ambassador applications'
      />

      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label>
          Status:&nbsp;
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <ButtonOBSOLETED onClick={load} disabled={isLoading} buttonContent='Refresh' />
      </div>

      {isLoading && <div style={{ padding: 16 }}>Loading…</div>}

      {!isLoading && items.length === 0 && (
        <div style={{ padding: 24, color: '#666' }}>
          No applications for status &ldquo;{status}&rdquo;.
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left', background: '#f5f5f5' }}>
              <th style={{ padding: 8 }}>Applicant</th>
              <th style={{ padding: 8 }}>Community</th>
              <th style={{ padding: 8 }}>Sport</th>
              <th style={{ padding: 8 }}>Contribution note</th>
              <th style={{ padding: 8, whiteSpace: 'nowrap' }}>Submitted</th>
              <th style={{ padding: 8, whiteSpace: 'nowrap' }}>Decided</th>
              <th style={{ padding: 8 }}>Status</th>
              <th style={{ padding: 8 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((app) => (
              <tr key={app.id} style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                <td style={{ padding: 8 }}>
                  <div><strong>{app.userName || '—'}</strong></div>
                  <div style={{ fontSize: 12, color: '#666' }}>{app.userEmail || ''}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{app.userPhone || ''}</div>
                </td>
                <td style={{ padding: 8 }}>
                  <div>{app.playAffiliationName}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{formatKind(app.playAffiliationKind)}</div>
                </td>
                <td style={{ padding: 8 }}>{formatSport(app.sportCategory)}</td>
                <td style={{ padding: 8, maxWidth: 320 }}>
                  {app.contributionNote ? (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{app.contributionNote}</span>
                  ) : (
                    <span style={{ color: '#999' }}>—</span>
                  )}
                </td>
                <td style={{ padding: 8, fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                  {formatDate(app.createdAtUtc)}
                </td>
                <td style={{ padding: 8, fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                  {formatDate(app.decidedAtUtc)}
                </td>
                <td style={{ padding: 8 }}>{formatStatus(app.status)}</td>
                <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                  {status === 'Pending' ? (
                    <>
                      <ButtonOBSOLETED
                        onClick={() => handleApprove(app.id)}
                        disabled={actingId === app.id}
                        buttonContent='Approve'
                      />
                      <span style={{ marginLeft: 8 }}>
                        <ButtonOBSOLETED
                          onClick={() => handleDecline(app.id)}
                          disabled={actingId === app.id}
                          buttonContent='Decline'
                        />
                      </span>
                    </>
                  ) : (
                    <span style={{ color: '#999' }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

AmbassadorApplicationsInternal.propTypes = {
  api: PropTypes.object.isRequired,
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(withRouter(withApi(AmbassadorApplicationsInternal)));
